/**
 * `/<tenantSlug>/admin/execution-log` — Admin Lab: Execution Log.
 *
 * Per ADR-046 Phase 1: searchable list of every agent_runs row for the
 * tenant, filterable by agent_type / status / outcome, with a per-row
 * detail panel that shows the full reasoning trace (every tool call in
 * sequence, plus the linked L3 memory_id).
 *
 * Backed by:
 *   GET /api/shell/admin/runs           — list with cursor pagination
 *   GET /api/shell/admin/runs/{id}/trace — full per-run trace
 *
 * Both endpoints route through the L7 `app_l7_admin_lab` pool whose
 * role has zero direct grants on `agent_runs` / `agent_tool_calls` —
 * the only path is the SECURITY DEFINER aggregator functions in
 * migration 0076, which re-validate the caller's permission slug
 * inside the function body (a stale JWT cannot bypass).
 *
 * Permission gate: `admin.execution-log.view`. Owner role gets this by
 * default; non-owners see a 403 banner.
 */

import { useApiClient } from "@ken/api-client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmpty,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from "@ken/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";

export const Route = createFileRoute("/$tenantSlug/admin/execution-log")({
  component: ExecutionLogRoute,
});

// ---------------------------------------------------------------------------
// API shapes — mirror the FastAPI Pydantic models in router_admin_lab.py
// ---------------------------------------------------------------------------

interface RunSummary {
  run_id: string;
  started_at: string;
  ended_at: string | null;
  agent_type: string;
  status: string;
  outcome: string | null;
  initiating_user_id: string | null;
  conversation_id: string | null;
  user_message: string | null;
  final_output: string | null;
  model_id: string;
  token_input_count: number;
  token_output_count: number;
  l3_memory_id: string | null;
}

interface RunsListResponse {
  items: RunSummary[];
  has_more: boolean;
  next_cursor_started_at: string | null;
  next_cursor_run_id: string | null;
}

interface ToolCall {
  call_sequence: number;
  call_id: string;
  tool_name: string;
  arguments: unknown;
  result: unknown;
  status: string;
  error_message: string | null;
  started_at: string;
  ended_at: string | null;
}

interface RunTraceResponse {
  run: RunSummary;
  tool_calls: ToolCall[];
}

interface ApiClientLike {
  GET: (path: string) => Promise<{ data?: unknown; response: Response }>;
}

async function fetchRuns(
  client: unknown,
  filters: { agent_type?: string; status?: string; outcome?: string },
): Promise<RunsListResponse> {
  const qs = new URLSearchParams();
  qs.set("limit", "50");
  if (filters.agent_type) qs.set("agent_type", filters.agent_type);
  if (filters.status) qs.set("status", filters.status);
  if (filters.outcome) qs.set("outcome", filters.outcome);
  const r = await (client as ApiClientLike).GET(
    `/api/shell/admin/runs?${qs.toString()}`,
  );
  if (!r.response.ok) throw new Error(`runs ${r.response.status}`);
  return r.data as RunsListResponse;
}

async function fetchTrace(
  client: unknown,
  runId: string,
): Promise<RunTraceResponse> {
  const r = await (client as ApiClientLike).GET(
    `/api/shell/admin/runs/${encodeURIComponent(runId)}/trace`,
  );
  if (!r.response.ok) throw new Error(`trace ${r.response.status}`);
  return r.data as RunTraceResponse;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function statusTone(status: string): "default" | "blue" | "green" | "red" | "amber" {
  if (status === "completed") return "green";
  if (status === "running") return "blue";
  if (status === "awaiting_approval") return "amber";
  if (status === "failed" || status === "cancelled") return "red";
  return "default";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ExecutionLogRoute(): ReactElement {
  const apiClient = useApiClient();
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const filters = {
    ...(agentTypeFilter ? { agent_type: agentTypeFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(outcomeFilter ? { outcome: outcomeFilter } : {}),
  };

  const runsQuery = useQuery({
    queryKey: ["admin", "runs", filters],
    queryFn: () => fetchRuns(apiClient, filters),
  });

  const traceQuery = useQuery({
    queryKey: ["admin", "trace", selectedRunId],
    queryFn: () => fetchTrace(apiClient, selectedRunId!),
    enabled: selectedRunId !== null,
  });

  const isForbidden =
    runsQuery.isError &&
    runsQuery.error instanceof Error &&
    runsQuery.error.message === "runs 403";

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="admin-execution-log-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
          Admin Lab
        </div>
        <h1 className="text-2xl font-semibold mt-1">Execution Log</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Every Concierge run, oldest cursor first. Click a row to see the
          full reasoning trace — recalled memories, tool dispatches, and
          the assistant&apos;s reply.
        </p>
      </header>

      {isForbidden ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-sm" role="alert">
              You don&apos;t have the <code>admin.execution-log.view</code>{" "}
              permission. Ask an owner to grant it on the Roles &amp;
              permissions page.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_4fr] gap-6">
          {/* ---- List pane ---------------------------------------- */}
          <Card data-testid="admin-execution-log-list">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Runs</CardTitle>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <input
                  data-testid="admin-execution-log-filter-agent-type"
                  placeholder="agent_type"
                  value={agentTypeFilter}
                  onChange={(e) => setAgentTypeFilter(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[oklch(0%_0_0_/_0.1)] text-xs"
                />
                <input
                  data-testid="admin-execution-log-filter-status"
                  placeholder="status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[oklch(0%_0_0_/_0.1)] text-xs"
                />
                <input
                  data-testid="admin-execution-log-filter-outcome"
                  placeholder="outcome"
                  value={outcomeFilter}
                  onChange={(e) => setOutcomeFilter(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[oklch(0%_0_0_/_0.1)] text-xs"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable>
                <DataTableHead>
                  <DataTableRow>
                    <DataTableHeader>Started</DataTableHeader>
                    <DataTableHeader>Agent</DataTableHeader>
                    <DataTableHeader>Status</DataTableHeader>
                    <DataTableHeader>Message</DataTableHeader>
                  </DataTableRow>
                </DataTableHead>
                <DataTableBody>
                  {runsQuery.isLoading ? (
                    <DataTableEmpty>Loading…</DataTableEmpty>
                  ) : runsQuery.data && runsQuery.data.items.length === 0 ? (
                    <DataTableEmpty>No runs match these filters.</DataTableEmpty>
                  ) : (
                    runsQuery.data?.items.map((run) => (
                      <DataTableRow
                        key={run.run_id}
                        data-testid={`admin-execution-log-row-${run.run_id}`}
                        onClick={() => setSelectedRunId(run.run_id)}
                        className={[
                          "cursor-pointer",
                          selectedRunId === run.run_id
                            ? "bg-[oklch(95%_0.02_240_/_0.6)]"
                            : "",
                        ].join(" ")}
                      >
                        <DataTableCell className="text-xs whitespace-nowrap">
                          {formatTimestamp(run.started_at)}
                        </DataTableCell>
                        <DataTableCell className="text-xs">
                          {run.agent_type}
                        </DataTableCell>
                        <DataTableCell>
                          <Chip tone={statusTone(run.status)}>{run.status}</Chip>
                        </DataTableCell>
                        <DataTableCell className="text-xs truncate max-w-[280px]">
                          {run.user_message ?? "—"}
                        </DataTableCell>
                      </DataTableRow>
                    ))
                  )}
                </DataTableBody>
              </DataTable>
            </CardContent>
          </Card>

          {/* ---- Detail pane -------------------------------------- */}
          <Card data-testid="admin-execution-log-detail">
            <CardHeader>
              <CardTitle className="text-base">Reasoning Trace</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedRunId === null ? (
                <div className="text-sm text-[var(--color-text-faint)]">
                  Select a run to see its full trace.
                </div>
              ) : traceQuery.isLoading ? (
                <div className="text-sm">Loading trace…</div>
              ) : traceQuery.isError ? (
                <div className="text-sm text-[var(--color-accent-red)]" role="alert">
                  Failed to load: {String(traceQuery.error)}
                </div>
              ) : traceQuery.data ? (
                <TraceDetail trace={traceQuery.data} />
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function TraceDetail({ trace }: { trace: RunTraceResponse }): ReactElement {
  const { run, tool_calls } = trace;

  return (
    <div className="flex flex-col gap-4" data-testid="admin-execution-log-trace-body">
      {/* Run-level header */}
      <section className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
        <div>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Run id
          </div>
          <div className="font-mono">{run.run_id.slice(0, 8)}…</div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Status
          </div>
          <Chip tone={statusTone(run.status)}>{run.status}</Chip>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Model
          </div>
          <div className="font-mono">{run.model_id}</div>
        </div>
        <div>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Tokens (in / out)
          </div>
          <div className="font-mono">
            {run.token_input_count} / {run.token_output_count}
          </div>
        </div>
        <div className="col-span-2">
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            Conversation
          </div>
          <div className="font-mono">{run.conversation_id ?? "—"}</div>
        </div>
        <div className="col-span-2">
          <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
            L3 memory id
          </div>
          <div className="font-mono">{run.l3_memory_id ?? "—"}</div>
        </div>
      </section>

      {/* User message */}
      <section>
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)] mb-1">
          User
        </div>
        <div className="text-sm whitespace-pre-wrap p-3 rounded-md bg-[oklch(95%_0.012_240_/_0.6)] border border-[var(--color-glass-border)]">
          {run.user_message ?? "—"}
        </div>
      </section>

      {/* Tool calls */}
      {tool_calls.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)] mb-1">
            Tool calls ({tool_calls.length})
          </div>
          <div className="flex flex-col gap-2">
            {tool_calls.map((tc) => (
              <div
                key={tc.call_id}
                data-testid={`admin-execution-log-tool-call-${tc.call_sequence}`}
                className="p-3 rounded-md border border-[var(--color-glass-border)] bg-white"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Chip tone={statusTone(tc.status)}>{tc.status}</Chip>
                  <code className="text-xs font-semibold">{tc.tool_name}</code>
                  <span className="text-[10px] text-[var(--color-text-faint)] ml-auto">
                    #{tc.call_sequence}
                  </span>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer">arguments</summary>
                  <pre className="mt-1 p-2 rounded bg-[oklch(98%_0.003_230_/_0.7)] overflow-x-auto">
                    {JSON.stringify(tc.arguments, null, 2)}
                  </pre>
                </details>
                <details className="text-xs mt-1">
                  <summary className="cursor-pointer">result</summary>
                  <pre className="mt-1 p-2 rounded bg-[oklch(98%_0.003_230_/_0.7)] overflow-x-auto max-h-64">
                    {JSON.stringify(tc.result, null, 2)}
                  </pre>
                </details>
                {tc.error_message !== null && (
                  <div className="mt-1 text-xs text-[var(--color-accent-red)]">
                    error: {tc.error_message}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Assistant final output */}
      <section>
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)] mb-1">
          Assistant
        </div>
        <div
          data-testid="admin-execution-log-trace-final-output"
          className="text-sm whitespace-pre-wrap p-3 rounded-md bg-white border border-[var(--color-glass-border)]"
        >
          {run.final_output ?? "—"}
        </div>
      </section>

      {/* Outcome chip */}
      {run.outcome !== null && (
        <div className="text-xs">
          outcome:{" "}
          <Chip
            tone={run.outcome === "success" ? "green" : "red"}
            data-testid="admin-execution-log-trace-outcome"
          >
            {run.outcome}
          </Chip>
        </div>
      )}
    </div>
  );
}

// Unused export silencer for noUnusedLocals — Button is intentionally
// imported for the future Refresh control we'll add when the
// auto-refresh interval lands.
void Button;
